/**
 * R13-C / R13-D — Pre-Room Preview + In-room "Edit Devices" flip-back.
 *
 * Two modes:
 *   - mode="entry"  (R13-C): runs BEFORE LiveKit connect. getUserMedia, shows
 *                            cam preview + mic meter + device pickers + name
 *                            input. "Enter Room" returns the chosen options
 *                            via onEnter(...). LiveKit is NOT touched here.
 *
 *   - mode="edit"   (R13-D): runs WHILE connected to LiveKit. Live preview
 *                            inside the preview pane uses a fresh
 *                            getUserMedia stream (so the user can see what
 *                            other participants would see if they switched).
 *                            Selecting a new device triggers onDeviceChange
 *                            which the parent (Room.jsx) wires up to
 *                            `localParticipant.replaceTrack(...)` so the
 *                            published stream hot-swaps without a black flash.
 *
 * Persisted in localStorage["sr-preview-devices"]:
 *   { camId, micId, speakerId, displayName }
 *
 * Spec testids:
 *   - pre-room-preview        (root)
 *   - pre-room-cam-select
 *   - pre-room-mic-select
 *   - pre-room-speaker-select
 *   - pre-room-name-input
 *   - pre-room-enter          (Enter Room — entry mode only)
 *   - pre-room-mic-meter
 *   - pre-room-edit-done      (Done — edit mode only)
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Mic, Volume2, Loader2, ArrowRight } from "lucide-react";
import DarkSelect from "../components/DarkSelect";

const STORAGE_KEY = "sr-preview-devices";

// R14.2-A — Safe AudioContext close. `Cannot close a closed AudioContext`
// is a real DOMException that Chrome throws if `.close()` is invoked twice
// on the same ctx (which happens here because two useEffects both held a
// ref to the same ctx via `analyserRef.current.context` and `ctx` closure).
// Guarding by `ctx.state` is fast and avoids the throw entirely.
function closeCtx(ctx, label = "audio") {
  if (!ctx) return;
  if (ctx.state === "closed") return;
  try {
    const p = ctx.close();
    if (p && typeof p.then === "function") {
      p.catch((e) => console.warn(`[${label}] close rejected:`, e?.message || e));
    }
  } catch (e) {
    console.warn(`[${label}] close threw:`, e?.message || e);
  }
}

function readPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writePersisted(partial) {
  try {
    const cur = readPersisted();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cur, ...partial }));
  } catch { /* */ }
}


export default function PreRoomPreview({
  mode = "entry",                    // "entry" | "edit"
  roomName = "",
  initialDisplayName = "",
  onEnter,                            // entry: (params) => void
  onDeviceChange,                     // edit: ({kind:"camera"|"microphone", deviceId}) => void
  onDone,                             // edit: () => void
}) {
  const persisted = readPersisted();
  const [displayName, setDisplayName] = useState(persisted.displayName || initialDisplayName || "");
  const [camId, setCamId] = useState(persisted.camId || "");
  const [micId, setMicId] = useState(persisted.micId || "");
  const [speakerId, setSpeakerId] = useState(persisted.speakerId || "");
  const [devices, setDevices] = useState({ cams: [], mics: [], speakers: [] });
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [permState, setPermState] = useState({ cam: "unknown", mic: "unknown" });
  const [micLevel, setMicLevel] = useState(0);    // 0..1
  const [camActive, setCamActive] = useState(false);
  const [micActive, setMicActive] = useState(false);

  const videoRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const acquireRef = useRef(null);

  // R14.3-A — Enumerate devices independent of stream state. Chrome will
  // return entries with empty labels if no media has been granted yet, but
  // we still want the dropdowns populated so the user can SEE that devices
  // exist; once a stream succeeds, labels populate and a second enumerate
  // fires automatically via the effect that watches [stream].
  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === "videoinput");
      const mics = list.filter((d) => d.kind === "audioinput");
      const speakers = list.filter((d) => d.kind === "audiooutput");
      // eslint-disable-next-line no-console
      console.log(`[preview] enumerateDevices count=${list.length} cam=${cams.length} mic=${mics.length} speaker=${speakers.length}`,
        list.map((d) => ({ k: d.kind, l: (d.label || "(no label)").slice(0, 24), id: d.deviceId.slice(0, 8) })));
      setDevices({ cams, mics, speakers });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[preview] enumerate failed:", err);
    }
  }, []);

  // 1. Mount: ask for cam+mic with whatever the user picked last (or default).
  //    On first try (no persisted device IDs), the browser prompts; subsequent
  //    swaps via the dropdowns just call getUserMedia with the new deviceId.
  //
  //    R14.3-A — if cam+mic fails for any reason (mic missing, only cam
  //    granted, etc.) we fall back to video-only so the preview is still
  //    useful. Errors are surfaced via the `error` state which renders a
  //    "Try Again" CTA.
  const acquire = useCallback(async (camIdArg, micIdArg) => {
    // eslint-disable-next-line no-console
    console.log(`[preview] requesting getUserMedia camIdArg=${camIdArg || "(default)"} micIdArg=${micIdArg || "(default)"}`);
    const videoConstraint = camIdArg ? { deviceId: { exact: camIdArg } } : true;
    const audioConstraintBase = { echoCancellation: true, noiseSuppression: true };
    const audioConstraint = micIdArg ? { ...audioConstraintBase, deviceId: { exact: micIdArg } } : audioConstraintBase;
    let s = null;
    try {
      s = await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: audioConstraint });
      // eslint-disable-next-line no-console
      console.log(`[preview] getUserMedia resolved (cam+mic), tracks=${s.getTracks().length}`,
        s.getTracks().map((t) => ({ kind: t.kind, label: t.label.slice(0, 24), state: t.readyState })));
    } catch (err1) {
      // eslint-disable-next-line no-console
      console.warn("[preview] cam+mic getUserMedia failed:", err1.name, err1.message);
      // Try video-only — useful if user has no mic or denied mic only.
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: false });
        // eslint-disable-next-line no-console
        console.log("[preview] video-only getUserMedia resolved (mic unavailable)");
      } catch (err2) {
        // eslint-disable-next-line no-console
        console.error("[preview] both cam+mic AND video-only failed:", err2.name, err2.message);
        setError(`${err2.name}: ${err2.message}`);
        setCamActive(false);
        setMicActive(false);
        // Even when getUserMedia fails, enumerate may still return empty-label
        // entries — refresh so the UI shows SOMETHING.
        await refreshDevices();
        return null;
      }
    }
    setStream((prev) => {
      // Stop the OLD stream's tracks so the camera light turns off if we
      // swapped cams.
      if (prev) prev.getTracks().forEach((t) => t.stop());
      return s;
    });
    setError(null);
    const vTrack = s.getVideoTracks()[0];
    const aTrack = s.getAudioTracks()[0];
    setCamActive(!!vTrack && vTrack.readyState === "live");
    setMicActive(!!aTrack && aTrack.readyState === "live");
    if (videoRef.current) {
      videoRef.current.srcObject = s;
      videoRef.current.play?.().catch(() => {});
    }
    // Persist whatever ID the browser actually gave us so later visits don't
    // re-prompt the user.
    if (vTrack?.getSettings) {
      const id = vTrack.getSettings().deviceId;
      if (id) { setCamId(id); writePersisted({ camId: id }); }
    }
    if (aTrack?.getSettings) {
      const id = aTrack.getSettings().deviceId;
      if (id) { setMicId(id); writePersisted({ micId: id }); }
    }
    // Re-enumerate now that labels are populated.
    await refreshDevices();
    return s;
  }, [refreshDevices]);

  // Keep a ref to acquire so permission-change handlers can re-trigger
  // without forcing a deps re-bind.
  useEffect(() => { acquireRef.current = acquire; }, [acquire]);

  // R14.3-A — Mount: 
  //   (a) Enumerate devices immediately (so dropdowns at least show "(no
  //       label)" entries before permission is granted, instead of empty).
  //   (b) Query the Permissions API for cam + mic state. Listen for changes
  //       — when the user grants permission AFTER an initial denial, we
  //       re-acquire automatically.
  //   (c) Listen for `devicechange` events to re-enumerate on hotplug.
  //   (d) Kick off the first getUserMedia. acquire() handles its own fallback.
  useEffect(() => {
    // (a) eager enumerate
    refreshDevices();

    // (b) permissions API — not all browsers support it, gracefully degrade
    const permListeners = [];
    if (navigator.permissions?.query) {
      ["camera", "microphone"].forEach((name) => {
        navigator.permissions.query({ name })
          .then((perm) => {
            // eslint-disable-next-line no-console
            console.log(`[preview] permission state ${name}=${perm.state}`);
            setPermState((p) => ({ ...p, [name === "camera" ? "cam" : "mic"]: perm.state }));
            const onChange = () => {
              // eslint-disable-next-line no-console
              console.log(`[preview] permission state ${name} changed → ${perm.state}`);
              setPermState((p) => ({ ...p, [name === "camera" ? "cam" : "mic"]: perm.state }));
              if (perm.state === "granted") {
                // Permission granted out-of-band — kick a fresh acquire +
                // enumerate so labels populate and dropdowns refresh.
                refreshDevices();
                acquireRef.current?.(camId || null, micId || null);
              }
            };
            perm.addEventListener("change", onChange);
            permListeners.push({ perm, onChange });
          })
          .catch(() => { /* unsupported name on this browser, ignore */ });
      });
    } else {
      // eslint-disable-next-line no-console
      console.log("[preview] navigator.permissions.query not supported, skipping permission listeners");
    }

    // (c) devicechange listener
    const onDeviceChange = () => {
      // eslint-disable-next-line no-console
      console.log("[preview] devicechange fired, re-enumerating");
      refreshDevices();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);

    // (d) initial acquire
    // eslint-disable-next-line no-console
    console.log("[preview] mounting, requesting first getUserMedia");
    acquire(persisted.camId || null, persisted.micId || null);

    return () => {
      // R14.2-A — see closeCtx() docs.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      closeCtx(analyserRef.current?.context, "pre-room/unmount");
      permListeners.forEach(({ perm, onChange }) => {
        try { perm.removeEventListener("change", onChange); } catch { /* */ }
      });
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Re-enumerate when stream changes (labels populate AFTER first
  // successful getUserMedia, so this gives us a second pass with labels).
  useEffect(() => {
    if (stream) refreshDevices();
  }, [stream, refreshDevices]);

  // 3. Mic level meter — Web Audio analyser fed by the acquired audio track.
  useEffect(() => {
    if (!stream) return;
    const aTrack = stream.getAudioTracks()[0];
    if (!aTrack) return;
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(new MediaStream([aTrack]));
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      src.connect(an);
      analyserRef.current = an;
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        // RMS amplitude
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setMicLevel(Math.min(1, rms * 4));    // sensitivity bump for visible meter
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn("[pre-room] mic meter setup failed:", err);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      closeCtx(ctx, "pre-room/mic-meter");
    };
  }, [stream]);

  // Device change handlers
  const onCamPick = async (id) => {
    setCamId(id);
    writePersisted({ camId: id });
    await acquire(id, micId);
    if (mode === "edit" && onDeviceChange) onDeviceChange({ kind: "camera", deviceId: id });
  };
  const onMicPick = async (id) => {
    setMicId(id);
    writePersisted({ micId: id });
    await acquire(camId, id);
    if (mode === "edit" && onDeviceChange) onDeviceChange({ kind: "microphone", deviceId: id });
  };
  const onSpeakerPick = (id) => {
    setSpeakerId(id);
    writePersisted({ speakerId: id });
    if (mode === "edit" && onDeviceChange) onDeviceChange({ kind: "speaker", deviceId: id });
  };
  const onName = (v) => {
    setDisplayName(v);
    writePersisted({ displayName: v });
  };

  // On mobile Chrome, permissions may not persist across page reloads even
  // after the user previously granted them. Allow entry with just a valid name
  // so users are not permanently blocked. LiveKit will request media again on
  // connect if needed.
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const mediaReady = camActive && micActive;
  const nameValid = mode === "edit" || displayName.trim().length >= 3;
  const enterEnabled = nameValid && (mediaReady || (isMobile && !error));

  const enter = () => {
    if (!enterEnabled) return;
    // Stop the local preview before handing off; the parent will start LiveKit
    // with the chosen device IDs.
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    onEnter?.({
      camId: camId || null,
      micId: micId || null,
      speakerId: speakerId || null,
      displayName: displayName.trim(),
    });
  };

  const done = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    // R15-3d — in-room rename: pass the current display name back to the
    // parent so it can apply it via LiveKit setName + setMetadata. The
    // parent stays the source of truth for the canonical name.
    onDone?.({ displayName: (displayName || "").trim() });
  };

  return (
    <div
      className="min-h-screen w-full bg-[#050508] text-white grid place-items-center p-4 sm:p-6"
      data-testid="pre-room-preview"
    >
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#0a0a14]/85 backdrop-blur shadow-2xl overflow-hidden">
        <header className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center">
            <Camera className="w-4 h-4 text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">
              {mode === "entry" ? "Joining room" : "Devices"}
            </div>
            <h1 className="text-base font-bold truncate">{roomName || "Full Spectrum Center"}</h1>
          </div>
          {mode === "edit" && (
            <button onClick={done} className="text-xs uppercase tracking-[0.14em] px-3 py-1.5 rounded border border-white/20 hover:bg-white/5"
                    data-testid="pre-room-edit-done">
              Done
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Camera preview */}
          <div className="relative bg-black aspect-video md:aspect-auto md:min-h-[320px]" data-testid="pre-room-cam-preview">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: "scaleX(-1)" }}
            />
            {!camActive && (
              <div className="absolute inset-0 grid place-items-center bg-black/85 text-white/85 text-xs gap-3 flex-col px-6 text-center">
                {error ? (
                  <>
                    <span className="text-rose-300 text-sm font-bold uppercase tracking-[0.2em]" data-testid="pre-room-blocked-title">
                      Camera / Mic blocked
                    </span>
                    <span className="opacity-75 max-w-xs">{error}</span>
                    <span className="opacity-55 max-w-xs text-[10px]">
                      Open the camera icon in your browser&apos;s URL bar, set Camera + Microphone to &quot;Allow&quot;, then click Try Again below.
                    </span>
                    <button
                      onClick={() => acquireRef.current?.(camId || null, micId || null)}
                      className="mt-1 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.16em] font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] hover:brightness-110 transition"
                      data-testid="pre-room-retry-btn"
                    >
                      Try Again
                    </button>
                    <span className="opacity-45 text-[10px]">
                      Cam: <span className="font-semibold text-white/70">{permState.cam}</span>
                      {" · "}
                      Mic: <span className="font-semibold text-white/70">{permState.mic}</span>
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Requesting camera…</span>
                    <span className="opacity-45 text-[10px]" data-testid="pre-room-perm-state">
                      Cam: <span className="font-semibold">{permState.cam}</span>
                      {" · "}
                      Mic: <span className="font-semibold">{permState.mic}</span>
                    </span>
                  </>
                )}
              </div>
            )}
            <div className="absolute bottom-3 left-3 right-3">
              <MicMeter level={micLevel} active={micActive} />
            </div>
          </div>

          {/* Controls panel */}
          <div className="p-5 space-y-3 flex flex-col">
            {/* R15-3d — Display name input is shown in BOTH entry and edit
                modes. In entry mode it's marked required and renders with
                magenta accent (required for first join). In edit mode the
                user can rename in-room: the parent (Room.jsx) reads the
                updated name on Done via the onDone({displayName}) payload
                and applies it via LiveKit setName + setMetadata. */}
            <div
              className={
                "rounded-lg border p-3 " +
                (mode === "entry"
                  ? "border-[#FF007F]/40 bg-[#FF007F]/[0.06]"
                  : "border-white/12 bg-white/[0.03]")
              }
            >
              <div className="flex items-baseline gap-2 mb-1.5">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/85">
                  {mode === "entry" ? "Display name" : "Rename"}
                </span>
                {mode === "entry" && (
                  <>
                    <span className="text-rose-300 text-base leading-none">*</span>
                    <span className="ml-auto text-[9px] uppercase tracking-[0.18em] text-rose-300 font-bold">Required</span>
                  </>
                )}
              </div>
              <input
                type="text"
                autoFocus={mode === "entry"}
                value={displayName}
                onChange={(e) => onName(e.target.value)}
                placeholder="Your name in the room"
                minLength={3}
                maxLength={48}
                className="w-full bg-black/35 border border-white/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#FF007F]/70 text-white"
                data-testid="pre-room-name-input"
              />
              <p className="text-[10px] text-white/40 mt-1.5 leading-snug">
                {mode === "entry"
                  ? (displayName.trim().length > 0 && displayName.trim().length < 3
                    ? <span className="text-amber-300/80">Name must be at least 3 characters.</span>
                    : "This is the name other participants will see on your tile.")
                  : "Change your display name. New name applies when you click Done."}
              </p>
            </div>

            <Field label="Camera" icon={<Camera className="w-3.5 h-3.5" />}>
              <DarkSelect
                value={camId}
                onChange={onCamPick}
                options={devices.cams.map((d) => ({ value: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 6)}` }))}
                placeholder="Select camera"
                testid="pre-room-cam-select"
              />
            </Field>

            <Field label="Microphone" icon={<Mic className="w-3.5 h-3.5" />}>
              <DarkSelect
                value={micId}
                onChange={onMicPick}
                options={devices.mics.map((d) => ({ value: d.deviceId, label: d.label || `Mic ${d.deviceId.slice(0, 6)}` }))}
                placeholder="Select mic"
                testid="pre-room-mic-select"
              />
            </Field>

            <Field label="Speaker" icon={<Volume2 className="w-3.5 h-3.5" />}>
              <DarkSelect
                value={speakerId}
                onChange={onSpeakerPick}
                options={devices.speakers.map((d) => ({ value: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 6)}` }))}
                placeholder="System default"
                testid="pre-room-speaker-select"
              />
            </Field>

            <div className="flex-1" />

            {mode === "entry" ? (
              <>
                <button
                  disabled={!enterEnabled}
                  onClick={enter}
                  className="w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] font-bold uppercase tracking-[0.14em] text-xs py-3 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                  data-testid="pre-room-enter"
                >
                  Enter Room <ArrowRight className="w-3.5 h-3.5" />
                </button>
                {isMobile && !mediaReady && !error && nameValid && (
                  <p className="text-[10px] text-amber-300/70 text-center mt-1">
                    Camera/mic still loading. You can enter now and grant access inside.
                  </p>
                )}
              </>
            ) : (
              <div className="text-[10px] text-white/40 italic text-center pt-1">
                Changes sync live to other participants.
              </div>
            )}
            {error && (
              <p className="text-[11px] text-rose-300 leading-snug">
                Browser denied media access. Click the camera icon in the URL bar to grant permission, then reload.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function Field({ label, icon, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1 flex items-center gap-1.5">
        {icon}{label}
      </div>
      {children}
    </label>
  );
}

// R14.3-B — The old internal `Select` (native <select>) was removed; all three
// device pickers above now render <DarkSelect> for a fully-controlled dark popup.

function MicMeter({ level, active }) {
  const pct = Math.min(100, Math.round(level * 100));
  return (
    <div className="bg-black/55 backdrop-blur rounded-lg px-3 py-2 border border-white/8" data-testid="pre-room-mic-meter">
      <div className="flex items-center gap-2 mb-1">
        <Mic className={`w-3 h-3 ${active ? "text-emerald-300" : "text-white/35"}`} />
        <span className="text-[10px] uppercase tracking-[0.16em] text-white/55">{active ? "Input" : "Mic off"}</span>
        <span className="ml-auto text-[10px] text-white/45 tabular-nums" data-testid="pre-room-mic-meter-value">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 via-amber-300 to-rose-400 transition-[width] duration-75"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
